import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useDropzone } from "react-dropzone";
import ThreeJSModel from "./ThreeJSModel";

const CustomizationForm = () => {
  const [showModel, setShowModel] = useState(true);
  const { control, handleSubmit, setValue, watch } = useForm({
    defaultValues: {
      height: 180, // cm
      weight: 80, // kg
      build: "athletic",
      text: "",
      image: null,
    },
  });

  const onSubmit = (data) => {
    console.log("Form submitted:", data);
    // Here you would typically send the data to your backend
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif']
    },
    onDrop: (acceptedFiles) => {
      setValue("image", acceptedFiles[0]);
    },
  });

  // Handle Alt+Q to toggle between image and 3D model
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.altKey && e.key === 'q') {
        setShowModel((prev) => !prev);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const currentHeight = watch("height");
  const currentWeight = watch("weight");
  const currentBuild = watch("build");
  const currentImage = watch("image");

  return (
    <div className="bg-black min-h-screen flex items-center justify-center py-12 px-6">
      <div className="bg-gray-900 shadow-2xl rounded-xl max-w-4xl w-full p-8 space-y-8 border border-purple-500/20">
        <h1 className="text-3xl font-bold text-center bg-gradient-to-r from-purple-400 to-blue-500 bg-clip-text text-transparent">
          T-Shirt Customizer
        </h1>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Left column: Form controls */}
          <div className="space-y-6">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* Dimensions Box */}
              <div className="border border-gray-700 rounded-lg p-6 bg-gray-800/50 backdrop-blur-sm">
                <h2 className="text-lg font-medium mb-4 text-gray-200">Body Measurements</h2>
                
                <div className="flex items-center mb-5">
                  <label htmlFor="height" className="text-sm font-medium w-24 text-gray-300">Height</label>
                  <div className="relative w-full flex items-center">
                    <Controller
                      name="height"
                      control={control}
                      render={({ field }) => (
                        <>
                          <div className="h-2 bg-gray-700 rounded-full w-full absolute"></div>
                          <div 
                            className="absolute top-0 left-0 h-2 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full pointer-events-none" 
                            style={{ width: `${((currentHeight - 150) / 70) * 100}%` }}
                          />
                          <input
                            type="range"
                            id="height"
                            {...field}
                            min="150"
                            max="220"
                            className="w-full h-2 bg-transparent rounded-full appearance-none cursor-pointer z-10 relative [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-purple-500 [&::-webkit-slider-thumb]:hover:bg-purple-600 [&::-webkit-slider-thumb]:relative [&::-webkit-slider-thumb]:z-20 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-purple-500 [&::-moz-range-thumb]:border-0 [&::-ms-thumb]:h-4 [&::-ms-thumb]:w-4 [&::-ms-thumb]:rounded-full [&::-ms-thumb]:bg-purple-500"
                          />
                        </>
                      )}
                    />
                  </div>
                  <span className="text-sm ml-2 w-16 text-right text-gray-300">{currentHeight} cm</span>
                </div>
                
                <div className="flex items-center mb-5">
                  <label htmlFor="weight" className="text-sm font-medium w-24 text-gray-300">Weight</label>
                  <div className="relative w-full flex items-center">
                    <Controller
                      name="weight"
                      control={control}
                      render={({ field }) => (
                        <>
                          <div className="h-2 bg-gray-700 rounded-full w-full absolute"></div>
                          <div 
                            className="absolute top-0 left-0 h-2 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full pointer-events-none" 
                            style={{ width: `${((currentWeight - 40) / 110) * 100}%` }}
                          />
                          <input
                            type="range"
                            id="weight"
                            {...field}
                            min="40"
                            max="150"
                            className="w-full h-2 bg-transparent rounded-full appearance-none cursor-pointer z-10 relative [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-purple-500 [&::-webkit-slider-thumb]:hover:bg-purple-600 [&::-webkit-slider-thumb]:relative [&::-webkit-slider-thumb]:z-20 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-purple-500 [&::-moz-range-thumb]:border-0 [&::-ms-thumb]:h-4 [&::-ms-thumb]:w-4 [&::-ms-thumb]:rounded-full [&::-ms-thumb]:bg-purple-500"
                          />
                        </>
                      )}
                    />
                  </div>
                  <span className="text-sm ml-2 w-16 text-right text-gray-300">{currentWeight} kg</span>
                </div>
                
                <div className="flex items-center">
                  <label htmlFor="build" className="text-sm font-medium w-24 text-gray-300">Build</label>
                  <Controller
                    name="build"
                    control={control}
                    render={({ field }) => (
                      <select
                        id="build"
                        {...field}
                        className="w-full bg-gray-800 border border-gray-700 rounded-md p-2 text-sm text-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      >
                        <option value="lean">Lean</option>
                        <option value="regular">Regular</option>
                        <option value="athletic">Athletic</option>
                        <option value="big">Big</option>
                      </select>
                    )}
                  />
                </div>
              </div>
              
              {/* Image Upload Section */}
              <div className="border border-gray-700 rounded-lg p-6 bg-gray-800/50 backdrop-blur-sm">
                <h2 className="text-lg font-medium mb-4 text-gray-200">Image Upload</h2>
                <div 
                  {...getRootProps()} 
                  className="border-2 border-dashed border-gray-600 rounded-lg p-6 text-center cursor-pointer hover:bg-gray-800/70 transition-colors group"
                >
                  <input {...getInputProps()} />
                  {currentImage ? (
                    <div className="flex flex-col items-center">
                      <img
                        src={URL.createObjectURL(currentImage)}
                        alt="Uploaded"
                        className="max-h-32 mb-3 rounded-md ring-2 ring-purple-500/50"
                      />
                      <p className="text-sm text-gray-400 group-hover:text-gray-200 transition-colors">Click or drag to replace</p>
                    </div>
                  ) : (
                    <div>
                      <div className="w-16 h-16 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:bg-purple-500/20 transition-colors">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-gray-400 group-hover:text-purple-400 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <p className="text-gray-400 group-hover:text-gray-200 transition-colors">Drag and drop your image here</p>
                      <p className="text-sm text-gray-500 mt-1 group-hover:text-gray-400 transition-colors">or click to select file</p>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Text for T-shirt */}
              <div className="border border-gray-700 rounded-lg p-6 bg-gray-800/50 backdrop-blur-sm">
                <h2 className="text-lg font-medium mb-4 text-gray-200">Custom Text</h2>
                <Controller
                  name="text"
                  control={control}
                  render={({ field }) => (
                    <textarea
                      {...field}
                      rows="3"
                      maxLength="100"
                      className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-gray-200 placeholder-gray-500"
                      placeholder="Enter text to print on your T-shirt (max 3 lines)"
                    />
                  )}
                />
                <div className="flex justify-between text-xs text-gray-500 mt-2">
                  <span>Max 100 characters</span>
                  <span>{watch("text")?.length || 0}/100</span>
                </div>
              </div>
              
              {/* Submit Button */}
              <button
                type="submit"
                className="w-full py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition duration-200 font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 focus:ring-offset-gray-900"
              >
                Submit Customization
              </button>
              
              <div className="flex items-center justify-center space-x-2 mt-3 text-sm text-gray-500">
                <kbd className="px-2 py-1 bg-gray-800 rounded-md text-gray-400 text-xs">Alt</kbd>
                <span>+</span>
                <kbd className="px-2 py-1 bg-gray-800 rounded-md text-gray-400 text-xs">Q</kbd>
                <span>to toggle between 2D/3D view</span>
              </div>
            </form>
          </div>
          
          {/* Right column: Preview */}
          <div className="flex flex-col">
            <div className="w-full p-6 border border-gray-700 rounded-lg bg-gray-800/50 h-full flex flex-col backdrop-blur-sm">
              <h2 className="text-lg font-medium mb-4 text-center text-gray-200">Preview</h2>
              
              <div className="relative flex-1 flex items-center justify-center overflow-hidden rounded-lg bg-gray-850">
                {showModel ? (
                  <div className="flex-1 flex items-center justify-center h-full">
                    <ThreeJSModel 
                      image={currentImage} 
                      text={watch("text")}
                      height={currentHeight}
                      weight={currentWeight}
                      build={currentBuild}
                    />
                  </div>
                ) : (
                  <div className="flex-1 flex items-center justify-center">
                    <div className="relative w-full max-w-xs">
                      <img 
                        src={currentImage ? URL.createObjectURL(currentImage) : "/default-tshirt.png"} 
                        alt="T-shirt Preview" 
                        className="w-full"
                      />
                      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center max-w-[80%] break-words">
                        {watch("text")}
                      </div>
                    </div>
                  </div>
                )}
                
                {/* View toggle button */}
                <button 
                  onClick={() => setShowModel(prev => !prev)}
                  className="absolute bottom-3 right-3 bg-gray-900/80 text-gray-300 p-2 rounded-full hover:bg-purple-500/80 transition-colors focus:outline-none"
                  title={showModel ? "Switch to 2D view" : "Switch to 3D view"}
                >
                  {showModel ? (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10l-2 1m0 0l-2-1m2 1v2.5M20 7l-2 1m2-1l-2-1m2 1v2.5M14 4l-2-1-2 1M4 7l2-1M4 7l2 1M4 7v2.5M12 21l-2-1m2 1l2-1m-2 1v-2.5M6 18l-2-1v-2.5M18 18l2-1v-2.5" />
                    </svg>
                  )}
                </button>
              </div>
              
              <div className="mt-4 bg-gray-850 rounded-lg p-4 border border-gray-700">
                <h3 className="font-medium text-center mb-3 text-gray-300">Current Configuration</h3>
                <div className="grid grid-cols-2 gap-2 text-sm text-gray-400">
                  <div className="flex items-center space-x-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    <span>{currentHeight} cm</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                    </svg>
                    <span>{currentWeight} kg</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <span className="capitalize">{currentBuild}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
                    </svg>
                    <span>{watch("text") ? "Added" : "None"}</span>
                  </div>
                  <div className="flex items-center space-x-2 col-span-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span>{currentImage ? "Uploaded" : "None"}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomizationForm;